import React, { useEffect, useRef, useState } from 'react';
import {
  Channel,
  Window,
  MessageList,
  MessageInput,
  useAttachmentManagerState,
  MessageListNotifications,
} from 'stream-chat-react';
import { EmojiPicker } from 'stream-chat-react/emojis';
import { AutoScrollToUnread } from './AutoScrollToUnread';
import { Box } from '@mui/material';
import { Profanity } from '@2toad/profanity';
import { badWords } from '@app/utils/BadWords';

const profanity = new Profanity({
  languages: ['en'],
  wholeWord: true,
});
profanity.addWords(badWords);

interface ChatConversationProps {
  channel: any;
  notify: () => Promise<void>;
}

const AttachmentLimitWarning: React.FC<{ onError: (msg: string) => void }> = ({ onError }) => {
  const { attachments } = useAttachmentManagerState();
  const notified = useRef<boolean>(false);

  useEffect(() => {
    if (attachments.length >= 10 && !notified.current) {
      onError('You may attach up to 10 files.');
      notified.current = true;
    }
    if (attachments.length < 10 && notified.current) {
      notified.current = false;
    }
  }, [attachments, onError]);

  return null;
};

export const ChatConversation: React.FC<ChatConversationProps> = ({ channel, notify }) => {
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Return focus and clear toasts
  useEffect(() => {
    if (!toastMessage) return;

    // Return focus to the input
    const input = document.querySelector('.str-chat__textarea textarea');
    if (input) {
      (input as HTMLTextAreaElement).focus();
    }

    // Clear toast message
    const timer = setTimeout(() => {
      setToastMessage(null);
    }, 3000);

    return () => clearTimeout(timer); // cleanup on unmount or message change
  }, [toastMessage]);

  return (
    <Box sx={{ flex: 1, overflow: 'auto' }}>
      <Channel channel={channel} EmojiPicker={EmojiPicker} MessageListNotifications={MessageListNotifications}>
        <AutoScrollToUnread />
        <Window>
          <AttachmentLimitWarning onError={(msg) => setToastMessage(msg)} />
          <MessageList messageActions={['react', 'quote', 'edit', 'delete', 'markUnread']} />

          {/* Toast messages */}
          {toastMessage && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'left',
                margin: '0 5px',
              }}
            >
              <Box
                sx={{
                  backgroundColor: '#3f3f3f',
                  color: '#fff',
                  padding: '20px',
                  borderRadius: '15px',
                  fontSize: '16px',
                  fontWeight: 400,
                  fontFamily: 'Roboto, sans-serif',
                  width: '100%',
                  textAlign: 'left',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                  lineHeight: 1.4,
                }}
              >
                {toastMessage}
              </Box>
            </Box>
          )}

          <MessageInput
            focus
            maxRows={8}
            minRows={1}
            overrideSubmitHandler={async ({ message, sendOptions }) => {
              // If no message text, no attachments, and no poll, do nothing
              if (
                !message.text?.trim() &&
                (!message.attachments || message.attachments.length === 0) &&
                !message.poll_id
              ) {
                return;
              }

              // Trim text to 5000 characters
              if (message.text && message.text.length > 5000) {
                message.text = message.text.slice(0, 5000);
                // Send a toast notification
                setToastMessage('Message text truncated to 5000 characters.');
              }

              // Check for profanity
              if (message.text && profanity.exists(message.text)) {
                // Censor profanity
                message.text = profanity.censor(message.text);
                // Send a toast notification
                setToastMessage('Profanity censored.');
              }

              // Send the message
              await channel.sendMessage(message, sendOptions);

              // Notify via provided callback
              await notify();
            }}
          />
        </Window>
      </Channel>
    </Box>
  );
};
